import React, { useState, useEffect, useCallback } from 'react';
import AddNetworkModal from './AddNetworkModal';
import SectionedDialog from './SectionedDialog';
import AccessSection from './AccessSection';
import { useProtectedResources } from './useProtectedResources';
import { ReactComponent as ShieldIcon } from './shield.svg';

/** Networks list view (multi-network slug Phase 3).
 *
 * Fetches GET /api/networks/, renders a table (canonical_name, display_name,
 * node_count, root_node_id, created). + Add Network button at top-right.
 * Click an existing row's display_name to inline-edit; canonical_name
 * gets the same affordance.
 *
 * No URL routing yet — Phase 4 wires selectedNetworkId into the URL
 * and switches TopologyView to filter on it.
 */
export default function NetworksView({ onSelectNetwork }) {
  const [networks, setNetworks] = useState(null);  // null = loading
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const { isProtected } = useProtectedResources();

  const refresh = useCallback(async () => {
    try {
      const r = await fetch('/api/networks/');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      const rows = (j && j.entity) || [];
      setNetworks(rows);
      setError(null);
    } catch (e) {
      setError(String(e.message || e));
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (error) {
    return <div style={{ padding: 24, color: '#c00' }}>Failed to load networks: {error}</div>;
  }
  if (networks === null) {
    return <div style={{ padding: 24, color: '#888' }}>Loading…</div>;
  }

  return (
    <div style={{ padding: '20px 24px', maxWidth: 960, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: '1.15rem' }}>Networks</h2>
        <span style={{ color: '#888', marginLeft: 12, fontSize: '0.85rem' }}>
          {networks.length === 1 ? '1 network' : `${networks.length} networks`}
        </span>
        <div style={{ flex: 1 }} />
        <button onClick={() => setShowAdd(true)} style={buttonPrimaryStyle}>
          + Add Network
        </button>
      </div>

      {networks.length === 0 ? (
        <div style={{ padding: 24, color: '#888', textAlign: 'center', border: '1px dashed #ddd', borderRadius: 4 }}>
          No networks yet. Click <strong>+ Add Network</strong> to create the first one.
        </div>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr style={{ background: '#f5f5f5' }}>
              <th style={thStyle}>Canonical name</th>
              <th style={thStyle}>Display name</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Nodes</th>
              <th style={thStyle}>Root</th>
              <th style={thStyle}>Created</th>
              <th style={{ ...thStyle, width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {networks.map((n) => {
              // core-network-tiers slug: a System network (its root node is
              // node_type='System') is the aveq core control plane — visible
              // to inspect, but not operator-managed (no rename/decommission;
              // no user nodes added to it).
              const isSystem = n.root_node_type === 'System';
              return (
              <tr key={n.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={tdStyle}>
                  {isProtected('network', n.id) && (
                    <span title="Access-controlled (protected)" aria-label="Protected network"
                          style={{ marginRight: 6, display: 'inline-flex', verticalAlign: 'middle' }}>
                      <ShieldIcon width={14} height={14} />
                    </span>
                  )}
                  {onSelectNetwork ? (
                    <button onClick={() => onSelectNetwork(n)} style={linkStyle}>
                      <code>{n.canonical_name}</code>
                    </button>
                  ) : (
                    <code>{n.canonical_name}</code>
                  )}
                  {isSystem && (
                    <span title="System network — the aveq core control plane. Inspect-only; not operator-managed."
                          style={{ marginLeft: 8, fontSize: '0.66rem', fontWeight: 600, letterSpacing: '0.04em',
                                   color: '#5a4b8a', background: '#ece8f6', border: '1px solid #cdc2e6',
                                   borderRadius: 3, padding: '1px 5px', verticalAlign: 'middle' }}>
                      SYSTEM
                    </span>
                  )}
                </td>
                <td style={tdStyle}>{n.display_name}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{n.node_count}</td>
                <td style={tdStyle}>
                  <NetworkRootBadge networkId={n.id} />
                </td>
                <td style={{ ...tdStyle, color: '#888', fontSize: '0.8rem' }}>
                  {n.created ? new Date(n.created).toLocaleString() : '—'}
                </td>
                <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                  {isSystem ? (
                    <span style={{ color: '#999', fontSize: '0.78rem' }}>core-managed</span>
                  ) : (
                    // Edit / Decommission moved into the per-network page's
                    // hamburger (next to the network name) — open the network.
                    <span style={{ color: '#aab', fontSize: '0.75rem' }}>open to manage</span>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {showAdd && (
        <AddNetworkModal
          onClose={() => setShowAdd(false)}
          onSuccess={() => { setShowAdd(false); refresh(); }} />
      )}

      {/* Rename / Decommission modals now live in the per-network page
          (App.js banner hamburger), reusing the exported modals below. */}
    </div>
  );
}


/** Edit network: names (PATCH /api/networks/{id} with whichever field changed; 409 = canonical_name
 * taken, 400 = reserved or malformed slug) and Access (protect / unprotect the whole network).
 */
export function RenameNetworkModal({ network, onClose, onSuccess }) {
  const [canonicalName, setCanonicalName] = useState(network.canonical_name);
  const [displayName, setDisplayName] = useState(network.display_name);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [activeCat, setActiveCat] = useState('network');

  const submit = async () => {
    const body = {};
    if (canonicalName.trim() !== network.canonical_name) body.canonical_name = canonicalName.trim();
    if (displayName.trim() !== network.display_name) body.display_name = displayName.trim();
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch(`/api/networks/${network.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const text = await r.text();
      let parsed = null;
      try { parsed = JSON.parse(text); } catch (_) {}
      if (!r.ok) {
        const hint = (
          r.status === 409 ? ' — canonical_name already in use; pick another' :
          r.status === 400 ? ' — slug is reserved or malformed' :
          r.status === 404 ? ' — network not found (refresh?)' :
          ''
        );
        throw new Error((parsed && (parsed.message || parsed.error) || text || `HTTP ${r.status}`) + hint);
      }
      onSuccess();
    } catch (e) {
      setActiveCat('network');
      setError(String(e.message || e));
    } finally {
      setSubmitting(false);
    }
  };

  const renderNetwork = () => (
    <div>
      <label style={labelStyle}>
        <span>Canonical name <small style={{ color: '#888' }}>(URL slug)</small></span>
        <input value={canonicalName} onChange={(e) => setCanonicalName(e.target.value)}
               maxLength={63} autoFocus style={inputStyle} />
        <small style={{ color: '#888', fontSize: '0.75rem' }}>
          Lowercase kebab-case. <code>api</code>, <code>chat</code>, <code>s</code>, <code>health</code>, <code>admin</code>, <code>hub</code> and a few others are reserved.
        </small>
      </label>
      <label style={labelStyle}>
        <span>Display name</span>
        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
               style={inputStyle} />
      </label>
    </div>
  );

  const categories = [
    { id: 'network', label: 'Network', description: 'Names.', render: renderNetwork },
    { id: 'access', label: 'Access',
      description: 'Protect / unprotect chat-user access to every station on this network.',
      render: () => <AccessSection level="network" id={network.id} name={network.display_name} /> },
  ];

  return (
    <SectionedDialog
      title="Edit network"
      categories={categories}
      activeId={activeCat}
      onSelect={setActiveCat}
      submitting={submitting}
      submittingLabel="Saving…"
      onPrimary={submit}
      onCancel={onClose}
      error={error}
      width={640}
    />
  );
}


/** Decommission modal — typed canonical-name confirm. DELETE
 * /api/networks/{id}?cascade=true tears down every node (root included),
 * the per-network container + its on-disk config, and the
 * <canonical>-member / -admin operator roles. No undo. A `partial`
 * response means the hub rows are gone but container/dir/role cleanup
 * needs a look — surfaced, but still a success (re-run finishes it). */
export function DecommissionNetworkModal({ network, onClose, onSuccess }) {
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch(`/api/networks/${network.id}?cascade=true`, { method: 'DELETE' });
      const text = await r.text();
      let parsed = null;
      try { parsed = JSON.parse(text); } catch (_) {}
      if (!r.ok) {
        const hint = r.status === 404 ? ' — network not found (refresh?)' : '';
        throw new Error((parsed && (parsed.message || parsed.error) || text || `HTTP ${r.status}`) + hint);
      }
      if (parsed && parsed.partial) {
        window.alert(
          'Network decommissioned, but some teardown steps need attention:\n\n' +
          JSON.stringify({ teardown: parsed.teardown, role_cleanup: parsed.role_cleanup }, null, 2));
      }
      onSuccess();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSubmitting(false);
    }
  };

  const armed = confirmText === network.canonical_name && !submitting;

  return (
    <div style={overlayStyle} onClick={() => !submitting && onClose()}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginTop: 0, color: '#c00' }}>Decommission network</h2>
        <p style={{ fontSize: '0.85rem' }}>
          This permanently tears down <strong>{network.display_name}</strong>{' '}
          (<code>{network.canonical_name}</code>) and everything under it:
        </p>
        <ul style={{ fontSize: '0.83rem', color: '#444', marginTop: 0 }}>
          <li>{network.node_count} node(s) — root included — and their stations + identities</li>
          <li>the per-network qbric container + its on-disk config</li>
          <li>the <code>{network.canonical_name}-member</code> and <code>{network.canonical_name}-admin</code> operator roles</li>
        </ul>
        <p style={{ fontSize: '0.85rem' }}>
          There is no undo. Type the canonical name{' '}
          <code style={{ background: '#f5f5f5', padding: '1px 4px' }}>{network.canonical_name}</code> to confirm:
        </p>
        <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
               autoFocus style={{ ...inputStyle, width: '100%', marginBottom: 12 }} />
        {error && <div style={{ color: '#c00', fontSize: '0.85rem', marginBottom: 12 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={buttonSecondaryStyle} disabled={submitting}>Cancel</button>
          <button type="button" onClick={submit} disabled={!armed}
                  style={{ ...buttonDangerStyle, opacity: armed ? 1 : 0.4 }}>
            {submitting ? 'Decommissioning…' : 'Decommission'}
          </button>
        </div>
      </div>
    </div>
  );
}


// --- styles (match AddNodeModal palette) ---

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.9rem',
};
const thStyle = {
  textAlign: 'left',
  padding: '8px 12px',
  fontWeight: 600,
  fontSize: '0.8rem',
  color: '#555',
  borderBottom: '1px solid #ddd',
};
const tdStyle = {
  padding: '10px 12px',
  verticalAlign: 'top',
};
const linkStyle = {
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  color: '#1a73e8',
  fontSize: 'inherit',
  fontFamily: 'inherit',
};

// network-helpers-admin-ui slug: declare-public editor. Curate which of a
// network's public-ELIGIBLE helpers (apps that registered with public=true) are
// actually exposed in its public_helpers list. Checked = public = callable as
// `<canonical>/<ns>:invoke` by nodes that import the network.
export function PublicHelpersModal({ network, onClose, onSuccess }) {
  const [eligible, setEligible] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`/api/networks/${network.id}/public-helpers`)
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (cancelled) return;
        setEligible(body && Array.isArray(body.eligible) ? body.eligible : []);
        setSelected(new Set(body && Array.isArray(body.public) ? body.public : []));
      })
      .catch((e) => { if (!cancelled) setError(String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [network.id]);

  const toggle = (name) => setSelected((prev) => {
    const s = new Set(prev);
    if (s.has(name)) s.delete(name); else s.add(name);
    return s;
  });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch(`/api/networks/${network.id}/public-helpers`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ public: Array.from(selected) }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      onSuccess();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSubmitting(false);
    }
  };

  // show eligible candidates + any currently-public name no longer eligible (so it can be unchecked)
  const rows = Array.from(new Set([...eligible, ...selected])).sort();

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>Public helpers — {network.display_name}</h2>
        <p style={{ color: '#778', fontSize: '0.85rem' }}>
          Checked helpers are <strong>public</strong> in <code>{network.canonical_name}</code> —
          callable as <code>{network.canonical_name}/&lt;ns&gt;:invoke</code> by nodes that import this network.
        </p>
        <form onSubmit={submit}>
          {loading ? (
            <div>Loading…</div>
          ) : rows.length === 0 ? (
            <div style={{ color: '#888', fontSize: '0.85rem' }}>
              No public-eligible helpers in this network. An app must register with <code>public=true</code> first.
            </div>
          ) : (
            <div style={{ maxHeight: 260, overflowY: 'auto', margin: '8px 0' }}>
              {rows.map((name) => (
                <label key={name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0', fontSize: '0.9rem' }}>
                  <input type="checkbox" checked={selected.has(name)} onChange={() => toggle(name)} />
                  <span>{name}</span>
                  {!eligible.includes(name) && (
                    <small style={{ color: '#a60' }}>(no longer eligible)</small>
                  )}
                </label>
              ))}
            </div>
          )}
          {error && <div style={{ color: '#c00', fontSize: '0.85rem', marginBottom: 12 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={buttonSecondaryStyle}>Cancel</button>
            <button type="submit" disabled={submitting || loading} style={buttonPrimaryStyle}>
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const modalStyle = {
  background: '#fff', padding: 20, borderRadius: 6, minWidth: 420, maxWidth: 560,
  maxHeight: '90vh', overflow: 'auto', boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};
const labelStyle = {
  display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12,
  fontSize: '0.85rem',
};
const inputStyle = {
  padding: '6px 8px', fontSize: '0.9rem', border: '1px solid #ccc', borderRadius: 4,
};
const buttonPrimaryStyle = {
  padding: '6px 14px', background: '#5fa67a', color: '#fff', border: 'none',
  borderRadius: 4, fontSize: '0.85rem', cursor: 'pointer',
};
const buttonSecondaryStyle = {
  padding: '6px 14px', background: 'transparent', color: '#5fa67a',
  border: '1px solid #5fa67a', borderRadius: 4, fontSize: '0.85rem', cursor: 'pointer',
};
const buttonDangerStyle = {
  padding: '6px 14px', background: '#c0392b', color: '#fff', border: 'none',
  borderRadius: 4, fontSize: '0.85rem', cursor: 'pointer',
};


/** Status pill for the per-network root container (dynamic-network-root slug).
 *  Fetches GET /api/networks/{id}/root-status once on mount; shows running /
 *  stopped / unknown with appropriate color. Tooltip shows container id.
 *  No auto-refresh — the badge is informational, not an action surface.
 */
function NetworkRootBadge({ networkId }) {
  const [status, setStatus] = useState(null);  // null = loading
  useEffect(() => {
    let cancelled = false;
    fetch(`/api/networks/${encodeURIComponent(String(networkId))}/root-status`)
      .then((r) => r.ok ? r.json() : null)
      .then((body) => { if (!cancelled) setStatus(body || { state: 'unknown' }); })
      .catch(() => { if (!cancelled) setStatus({ state: 'unknown' }); });
    return () => { cancelled = true; };
  }, [networkId]);

  if (status === null) {
    return <span style={{ color: '#aaa', fontSize: '0.75rem' }}>…</span>;
  }
  const state = status.state || 'unknown';
  const palette = {
    running: { bg: '#dcf2dd', fg: '#1a5a1f', label: 'running' },
    stopped: { bg: '#f5e0e0', fg: '#7a1a1a', label: 'no container' },
    unknown: { bg: '#eee',    fg: '#666',    label: 'unknown' },
  }[state] || { bg: '#fbe7c0', fg: '#7a4a1a', label: state };
  const title = status.container_name
    ? `${palette.label} — ${status.container_name}${status.container_id ? ` (${status.container_id.slice(0,12)})` : ''}`
    : palette.label;
  return (
    <span title={title} style={{
      background: palette.bg, color: palette.fg, padding: '2px 8px',
      borderRadius: 10, fontSize: '0.7rem', fontWeight: 500,
      letterSpacing: '0.02em',
    }}>
      {palette.label}
    </span>
  );
}
